import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import {
  Button,
  Icon,
  Container,
  Text,
  Input,
  AuthBackdrop,
  FormAlert,
} from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';

import api from '../utils/axios';
import { isValidEmail } from '../utils/validators';
import { useTheme } from '../contexts/ThemeContext';
import veyloWordmarkBlack from '../assets/brand/veylo-wordmark-black.svg';
import veyloWordmarkWhite from '../assets/brand/veylo-wordmark-white.svg';

interface LocationState {
  email?: string;
  message?: string;
}

export const VerifyEmailPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState;
  const { theme } = useTheme();

  const [email, setEmail] = useState(state?.email || '');
  const [isCheckingVerification, setIsCheckingVerification] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [message, setMessage] = useState(state?.message || '');
  const [error, setError] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [emailTouched, setEmailTouched] = useState(false);
  const [buttonCooldown, setButtonCooldown] = useState(0);

  // Check if email was provided via navigation state
  const isEmailPrefilled = Boolean(state?.email);

  // Get email error message
  const getEmailError = (): string => {
    if (!emailTouched) return '';
    if (!email) return 'Email is required';
    if (!isValidEmail(email)) return 'Please enter a valid email address';
    return '';
  };

  // Cooldown timer effect for resend
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(
        () => setResendCooldown(resendCooldown - 1),
        1000
      );
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Cooldown timer effect for button
  useEffect(() => {
    if (buttonCooldown > 0) {
      const timer = setTimeout(
        () => setButtonCooldown(buttonCooldown - 1),
        1000
      );
      return () => clearTimeout(timer);
    }
  }, [buttonCooldown]);

  // Check verification status on mount if email is provided
  useEffect(() => {
    if (email) {
      checkVerificationStatus();
    }
  }, []);

  const checkVerificationStatus = async () => {
    if (!email) return;

    try {
      const response = await api.post('/auth/check-verification', { email });
      if (response.data.is_verified) {
        setIsVerified(true);
        setMessage('Your email has been verified successfully!');
        setError('');
      }
    } catch (error: any) {
      // Don't show error for verification check, just continue
      console.log('Verification check failed:', error);
    }
  };

  const handleCheckVerification = async () => {
    if (!email) {
      setError('Email address is required');
      return;
    }

    setIsCheckingVerification(true);
    setError('');
    setMessage('');

    try {
      const response = await api.post('/auth/check-verification', { email });
      if (response.data.is_verified) {
        setIsVerified(true);
        setMessage('Your email has been verified successfully!');
        setError('');
      } else {
        setError('Email is not yet verified. Please check your inbox.');
      }
    } catch (error: any) {
      const errMsg =
        error.response?.data?.detail || 'Failed to check verification status';
      setError(errMsg);
    } finally {
      setIsCheckingVerification(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      setError('Email address is required');
      return;
    }

    if (!isValidEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsResending(true);
    setError('');
    setMessage('');

    try {
      const response = await api.post('/auth/resend-verification', { email });
      setMessage(response.data.message);
      setResendCooldown(60); // 60 second cooldown
      setButtonCooldown(60); // 60 second cooldown for button
    } catch (error: any) {
      const errMsg =
        error.response?.data?.detail || 'Failed to resend verification email';
      setError(errMsg);
      // Don't set button cooldown on error
    } finally {
      setIsResending(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    setEmailTouched(true);

    // Clear messages when user types
    if (error) setError('');
    if (message) setMessage('');
    setIsVerified(false);
  };

  const handleDismissError = () => {
    setError('');
  };

  const handleDismissMessage = () => {
    setMessage('');
  };

  return (
    <div className="relative min-h-screen bg-bg-tertiary">
      <AuthBackdrop still />
      <div className="relative z-10 grid grid-cols-1 md:grid-cols-2">
        <div aria-hidden="true" className="hidden md:block" />
        <div>
          <Container variant={'transparent'} padding={'xl'} className="min-h-screen flex items-center justify-center">
            <Container padding={'4xl'} className="w-full max-w-md flex flex-col gap-bg-v2-xl rounded-bg-v2-md container-shadow-lg hover-scale-sm page-transition-enter">
              <div className="flex items-center justify-center gap-bg-v2-md animate-initial-hidden animate-fade-in-down animate-delay-200">
                <img src={theme === 'dark' ? veyloWordmarkWhite : veyloWordmarkBlack} alt="Veylo" className="h-10 w-auto object-contain" />
              </div>
              <Text variant={'display-xs'} weight="medium" className="font-bg-v2-display animate-initial-hidden animate-fade-in-up animate-delay-300" color="primary" align="center" as="p">
                {isVerified ? 'Email Verified Successfully!' : 'Verify Your Email'}
              </Text>
              
              {!isVerified && (
                <Text variant={'text-sm'} color="quaternary" align="center" className={'font-bg-v2-sans'} weight={'medium'} as="p">
                  {isEmailPrefilled 
                    ? ""
                    : "Enter your email address to check verification status or resend the verification link."}
                </Text>
              )}

              <FormAlert
                message={error}
                variant="error"
                onDismiss={handleDismissError}
              />
              
              {!isVerified && message && (
                <FormAlert
                  message={message}
                  variant="success"
                  onDismiss={handleDismissMessage}
                />
              )}

              {isVerified && message && (
                <FormAlert
                  message={message}
                  variant="success"
                  onDismiss={handleDismissMessage}
                />
              )}

              <form className="flex flex-col w-full gap-bg-v2-2xl" onSubmit={(e) => e.preventDefault()} noValidate>
                {!isVerified && (
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    label={
                      <div className="flex justify-between items-center w-full">
                        <span>Email Address</span>
                      </div>
                    }
                    placeholder="Enter your email address"
                    required
                    errorText={getEmailError()}
                    startIcon={<Icon name={IconName.Mail} size="md" />}
                    value={email}
                    onChange={handleEmailChange}
                    onBlur={() => setEmailTouched(true)}
                    disabled={isCheckingVerification || isResending || isEmailPrefilled || buttonCooldown > 0}
                  />
                )}

                {!isVerified ? (
                  <>
                    <div>
                      <Button
                        variant={'primary'}
                        type={'button'}
                        loading={isCheckingVerification}
                        disabled={isCheckingVerification || isResending || !email || !isValidEmail(email)}
                        onClick={handleCheckVerification}
                        className={'w-full flex justify-center items-center hover-lift'}
                        label={'Check Verification Status'}
                      />
                    </div>
                    <div>
                      <Button
                        variant={'outline'}
                        type={'button'}
                        loading={isResending}
                        disabled={isCheckingVerification || isResending || !email || !isValidEmail(email) || buttonCooldown > 0}
                        onClick={handleResendVerification}
                        className={'w-full flex justify-center items-center hover-lift'}
                        label={buttonCooldown > 0 ? `Resend in ${buttonCooldown}s` : 'Resend Verification Email'}
                      />
                    </div>
                  </>
                ) : (
                  <div>
                    <Button
                      variant={'primary'}
                      type={'button'}
                      onClick={() => navigate('/login')}
                      className={'w-full flex justify-center items-center hover-lift'}
                      label={'Continue to Login'}
                    />
                  </div>
                )}

                {!isVerified && (
                  <>
                    <div className="flex items-center justify-center gap-bg-v2-sm">
                      <Link
                        to="/login"
                        className="inline-flex items-center gap-bg-v2-xs text-bg-v2-sm font-bg-v2-medium text-bg-brand-solid hover:opacity-80 transition-smooth"
                      >
                        <Icon name={IconName.ArrowLeft} size="sm" inheritColor />
                        Back to Login
                      </Link>
                    </div>

                    <div className="flex flex-col gap-bg-v2-md">
                      <Text variant={'text-sm'} color="tertiary" align="center" className={'font-bg-v2-sans'} weight={'regular'} as="p">
                        Didn't receive the email? Check your spam folder.
                      </Text>
                      <Text variant={'text-sm'} color="secondary" align="center" className={'font-bg-v2-sans'} weight={'regular'} as="p">
                        Still having some trouble?
                        <a href="mailto:contact@veylo.com" className="font-bg-v2-sans font-bg-v2-medium text-bg-brand-solid hover:opacity-80 px-bg-v2-sm transition-smooth">
                          Contact support
                        </a>
                      </Text>
                    </div>
                  </>
                )}
              </form>
            </Container>
          </Container>
        </div>
      </div>
    </div>
  );
};

